import { useRef, useState, useEffect } from 'react';
import { Box, CircularProgress, Grid2 } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import QRCode from 'qrcode';
import CustomSnackbar from '@ui-components/Snackbar/CustomSnackbar';
import CustomCard from '@ui-components/Card/CustomCard';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomButton from '@ui-components/Button/CustomButton';
import { useAuth } from '../components/AuthContext';
import QLerLogo from '@assets/images/qler_Logo.svg';
import type {
  MFACodeValidationResponse,
  OtpType,
} from '../../API/services/twoFactorAuthService';
import {
  useGetMFADetails,
  useValidateMFAToken,
} from '../../API/hooks/twoFactorAuthHook';
import type { AlertColor } from '@mui/material';
import {
  CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH,
  LOGIN_ROUTE_PATH,
} from '../../routers/RoutPath';

const initialValues = {
  otp: '',
};

const TwoFactorAuthentication = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const MessageRef = useRef('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const pendingUserData = sessionStorage.getItem('pendingMfaUser');
  const user = pendingUserData ? JSON.parse(pendingUserData) : null;
  const isMFAScanRequired = user?.mfaScanRequired ?? false;
  const [messageType, setMessageType] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const { mutate, data } = useGetMFADetails();
  const [isScanLoaded, setIsScanLoaded] = useState(false);

  useEffect(() => {
    if (!pendingUserData) {
      navigate(LOGIN_ROUTE_PATH, { replace: true });
    }
  }, [pendingUserData, navigate]);

  useEffect(() => {
    if (!user?.userId || !user?.username) return;

    mutate(
      {
        userId: user.userId,
        username: user.username,
      },
      {
        onSuccess: () => {
          setIsScanLoaded(true);
        },
      }
    );
  }, []);

  const { mutate: mutateQrcode } = useValidateMFAToken();

  useEffect(() => {
    if (!data?.secretKey || !user) return;

    const timer = setTimeout(() => {
      if (!canvasRef.current) return;
      const qrValue = `otpauth://totp/Qler.com?secret=${data.secretKey}&issuer=${user.username}`;
      QRCode.toCanvas(canvasRef.current, qrValue, { width: 135 });
    }, 0);

    return () => clearTimeout(timer);
  }, [data?.secretKey, user?.username]);

  const validationSchema = Yup.object({
    otp: Yup.string()
      .required('Enter a valid OTP')
      .min(6, 'Enter a valid OTP')
      .max(6, 'Enter a valid OTP'),
  });

  const handleQrcodePassword = (request: OtpType) => {
    mutateQrcode(
      {
        otp: request.otp,
      },
      {
        onSuccess: (data: MFACodeValidationResponse) => {
          if (data.code === 200) {
            if (user) {
              const authenticatedUser = {
                ...user,
                accessToken: data.accessToken,
              };
              login(authenticatedUser);
              sessionStorage.removeItem('pendingMfaUser');
            }
            navigate(CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH, {
              replace: true,
            });
          }
        },

        onError: (error: Error | MFACodeValidationResponse) => {
          setOpenToast(true);
          setMessageType('error');
          MessageRef.current = error.message;
          setTimeout(() => {
            setOpenToast(false);
            MessageRef.current = '';
          }, 8000);
        },
      }
    );
  };
  const handleClose_SnackBar = () => {
    setOpenToast(false);
  };

  return (
    <Box className="container">
      {openToast && (
        <CustomSnackbar
          id="login-toast"
          successOrError={messageType as AlertColor}
          open={openToast}
          handleClose={handleClose_SnackBar}
          toastrMessage={MessageRef.current}
        />
      )}
      <Box className="center-div">
        <Box className="logo-qler">
          <QLerLogo />
        </Box>
        <Formik
          initialValues={initialValues}
          validateOnMount={true}
          enableReinitialize={true}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            handleQrcodePassword(values);
          }}
        >
          {({
            handleSubmit,
            setFieldValue,
            values,
            errors,
            touched,
            validateField,
          }) => (
            <Grid2 container justifyContent="center">
              <CustomCard
                className="card-width login-form"
                id="cardWithoutImage"
              >
                <CustomTypography
                  className="pb-12 mt-40"
                  id="scanqrcodetitle"
                  variant="h3"
                >
                  {isMFAScanRequired ? 'Scan the QR below' : 'Enter OTP'}
                </CustomTypography>
                <CustomTypography
                  id="SupportText"
                  variant="body2"
                  color="textSecondary"
                  className="pb-20"
                >
                  {isMFAScanRequired
                    ? 'Scan this QR code to add your QLER View account in your authenticator app.'
                    : 'Enter the OTP from your authenticator app.'}
                </CustomTypography>
                {isMFAScanRequired && (
                  <Box className="pb-24">
                    {isScanLoaded ? (
                      <canvas ref={canvasRef} />
                    ) : (
                      <CircularProgress id="circularProgress" size={25} />
                    )}
                  </Box>
                )}
                <Box className="pb-24">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                    <CustomTextField
                      id="txtEnterOtp"
                      variant="outlined"
                      placeholder="Enter OTP"
                      value={values.otp}
                      onlyDigits
                      onChange={(e) => {
                        setFieldValue('otp', e.target.value);
                        validateField('otp'); // Trigger validation
                      }}
                      size="small"
                    />
                    {errors.otp && touched.otp && (
                      <CustomTypography
                        id="error-username"
                        variant="body3"
                        color="error"
                        className="pt-8 float-left pb-8"
                      >
                        <ErrorMessage name="otp" />
                      </CustomTypography>
                    )}
                  </Grid2>
                </Box>

                <Box>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                    <CustomButton
                      id="btnSubmit"
                      variant="contained"
                      size="large"
                      className="button-width button-height button-font-size"
                      onClick={() => handleSubmit()}
                    >
                      Submit
                    </CustomButton>
                  </Grid2>
                </Box>
              </CustomCard>
            </Grid2>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default TwoFactorAuthentication;
